import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient.web";
import { authCodeFor, authMessageFor } from "../lib/errorCodes";
import Turnstile, { type TurnstileHandle } from "../lib/Turnstile";
import BrandHeader from "../lib/BrandHeader";

const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID as string | undefined;

/**
 * Delete an account. This page is the point of no return, and says so.
 *
 * ---------------------------------------------------------------------------
 * WHY THE PASSWORD IS TYPED HERE AND NOT IN THE POPUP.
 *
 * Deletion is gated on the account password (9.5): what reaches
 * `/account/delete` is a token whose `amr` claim says a password was presented.
 * Phase 10 enabled Supabase's built-in captcha, which is a GLOBAL GoTrue
 * setting - measured, `/token?grant_type=password` now requires a captcha token
 * - and an MV3 extension page cannot load Cloudflare's widget script under any
 * CSP it is allowed to declare. So the password moves to a page that can show
 * one.
 *
 * NOTHING ABOUT THE SECURITY PROPERTY CHANGES. The password still goes straight
 * to Supabase Auth and never through code this project wrote; the server-side
 * `amr` check is untouched. What changes is which window the typing happens in.
 *
 * ---------------------------------------------------------------------------
 * THIS PAGE DOES NOT PERFORM THE DELETION, AND MUST NOT.
 *
 * It would be one line to call `/account/delete` from here. It must not,
 * because deletion is two irreversible acts in a required order (9.5): the
 * server destroys the account, and only THEN does the device destroy the only
 * copy of the keys. Wiping first leaves an account nobody can ever open again.
 *
 * That ordering lives in the extension's `accountDeletion.ts`, where a test can
 * reach it and this page cannot import it. So this page hands the token to the
 * service worker and reports what comes back.
 *
 * A useful consequence: with no extension installed, NOTHING is deleted,
 * because the thing that deletes never runs. The page says exactly that rather
 * than leaving someone guessing.
 *
 * ---------------------------------------------------------------------------
 * THE COPY IS PART OF THE DESIGN.
 *
 * An earlier version of this page said "Nothing is deleted on this page - you
 * will finish in the extension", because it only collected a confirmation. That
 * is now false and would be false in the dangerous direction. Submitting this
 * form IS the deletion, so every piece of text here says so: the heading, the
 * warning, the line above the button, and the button itself.
 * ---------------------------------------------------------------------------
 */
export default function DeleteAccountPage() {
  const emailFromQuery = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get("email") ?? "";
  }, []);

  const [email, setEmail] = useState(emailFromQuery);
  const [password, setPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [done, setDone] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const turnstile = useRef<TurnstileHandle | null>(null);

  /**
   * The extension told us which account it holds, so this is NOT a field.
   *
   * An editable address is a way to delete the wrong account: sign in as B
   * while the extension holds A, and B is destroyed while A's device is wiped.
   * The extension refuses that outright - it compares the token's subject
   * against its own session - but a field that can only ever produce a refusal
   * should not be a field.
   *
   * Editable only when no address was supplied, which is someone reaching this
   * page directly. The extension's check still holds there.
   */
  const emailIsFixed = emailFromQuery.trim().length > 0;

  const canSubmit = !!email.trim() && !!password && !!captchaToken && !busy && !waiting;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setWarning(null);

    if (!canSubmit) return;

    if (!EXTENSION_ID) {
      setError(
        "Missing VITE_EXTENSION_ID. Add it to apps/web/.env — without it this page cannot reach the extension."
      );
      return;
    }

    setBusy(true);

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
      options: { captchaToken: captchaToken ?? undefined },
    });

    setBusy(false);

    // The captcha token is spent whether or not the sign-in worked, so the
    // widget is reset before the button can be pressed again. Without this a
    // retry fails with `timeout-or-duplicate` and reads as a wrong password.
    turnstile.current?.reset();
    setCaptchaToken(null);

    if (error || !data.session?.access_token) {
      // In this app's words, from the code (Phase 12). A wrong password keeps this
      // page's own sentence, because here the point is that nothing happened.
      const code = authCodeFor(error);
      setError(
        !error || code === "INVALID_CREDENTIALS"
          ? "That password was not accepted. Nothing has been deleted."
          : `${authMessageFor(code)} Nothing has been deleted.`
      );
      return;
    }

    // The password is not kept for a moment longer than it took to send.
    setPassword("");

    window.postMessage(
      {
        type: "TAKENOTE_DELETE_ACCOUNT",
        payload: { access_token: data.session.access_token },
      },
      window.location.origin
    );

    setWaiting(true);
  }

  // The content script replies with `${type}_RESULT` once the service worker
  // has finished. `ok` means the account is gone AND this device is clean.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== window) return;
      if (event.origin !== window.location.origin) return;

      const msg = event.data;
      if (!msg || msg.type !== "TAKENOTE_DELETE_ACCOUNT_RESULT") return;

      setWaiting(false);

      const res = msg.response;

      if (res?.ok) {
        setDone(true);
        return;
      }

      // WIPE_FAILED is the one outcome that is NOT a failure of the deletion.
      // The account is gone; only the local cleanup did not finish. Reporting
      // it as an error would tell someone their account still exists when it
      // does not, which is the wrong direction to be wrong in.
      if (res?.reason === "WIPE_FAILED") {
        setDone(true);
        setWarning(res.message ?? null);
        return;
      }

      setError(res?.message ?? res?.error ?? "The account was not deleted.");
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Nothing answered. The extension is the only thing that can, and it is also
  // the only thing that deletes - so silence means nothing happened.
  useEffect(() => {
    if (!waiting) return;

    const timer = setTimeout(() => {
      setWaiting(false);
      setError(
        "The TakeNote extension did not respond, so nothing has been deleted. Check it is installed and enabled, then try again."
      );
    }, 6000);

    return () => clearTimeout(timer);
  }, [waiting]);

  return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-surface shadow p-6">
        <BrandHeader />
        {done ? (
          <>
            <h1 className="text-xl font-semibold">Your account has been deleted</h1>

            <div className="mt-4 rounded-xl border border-success-line bg-success-bg p-3 text-sm text-success">
              {email ? <span className="font-mono">{email}</span> : "Your account"} is gone.
              Your profile, your keys, every note waiting for you and your counters were all
              removed. This cannot be undone.
            </div>

            {warning && (
              <div className="mt-3 rounded-xl border border-warning-line bg-warning-bg p-3 text-sm text-warning">
                {warning}
              </div>
            )}

            <div className="mt-4 space-y-2 text-sm text-fg-body">
              <p>
                Your username is kept forever as an unreadable fingerprint, so nobody can ever
                take your name and send notes that appear to be from you. It is linked to
                nothing else.
              </p>
              <p>
                The TakeNote extension is signed out and empty. You can close this tab.
              </p>
            </div>

            <div className="mt-6 text-sm">
              <Link className="text-link hover:underline" to="/auth/signup">
                Create a new account
              </Link>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold">Delete your account</h1>

            {/*
              The strongest statement on the page, above everything else,
              because it is the thing a person skimming has to not miss.
            */}
            <div className="mt-4 rounded-xl border-2 border-danger-line-strong bg-danger-bg p-3 text-sm text-danger-strong">
              <p className="font-semibold">
                Entering your password below deletes your account immediately.
              </p>
              <p className="mt-2">
                There is no confirmation step after this and no undo. Your profile, your keys
                and every note waiting for you are destroyed the moment you press the button.
                Your username can never be used again, by you or anyone else.
              </p>
            </div>

            <form className="mt-4 space-y-3" onSubmit={onSubmit}>
              <div>
                {/* Fixed from the extension, the account is shown as text, not a box: there is
                    no control for a label to name, and a label that names nothing is what
                    this page had. So the heading is a label only when there is a box. */}
                {emailIsFixed ? (
                  <>
                    {/* A span, as the label was inline: the same line, so nothing shifts. */}
                    <span className="text-sm font-medium">Account being deleted</span>
                    <div className="mt-1 w-full rounded-xl border border-line bg-panel px-3 py-2 font-mono text-sm text-fg-panel">
                      {email}
                    </div>
                  </>
                ) : (
                  <>
                    <label className="text-sm font-medium" htmlFor="email">Account being deleted</label>
                    <input
                      id="email"
                      className="mt-1 w-full rounded-xl border border-line-control px-3 py-2"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </>
                )}
                {emailIsFixed && (
                  <div className="mt-1 text-xs text-fg-hint">
                    To delete a different account, start from that account's extension.
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium" htmlFor="password">Account password</label>
                <input
                  id="password"
                  className="mt-1 w-full rounded-xl border border-line-control px-3 py-2"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                {/* 9.5: the PASSPHRASE is deliberately not what is asked for
                    here. The two secrets guard different blast radii, and
                    someone who has forgotten their passphrase must still be
                    able to delete their account rather than leaving a backend
                    row holding their username forever. */}
                <div className="mt-1 text-xs text-fg-hint">
                  This is the password you log in with — not your passphrase or PIN.
                </div>
              </div>

              <div className="pt-1">
                <Turnstile ref={turnstile} onToken={setCaptchaToken} />
              </div>

              {error && (
                <div className="rounded-xl border border-danger-line bg-danger-bg p-3 text-sm text-danger">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="mt-2 w-full rounded-xl bg-danger text-on-danger py-2.5 font-medium disabled:opacity-60"
              >
                {busy
                  ? "Checking your password…"
                  : waiting
                    ? "Deleting your account…"
                    : "Delete my account permanently"}
              </button>

              <p className="text-center text-xs text-fg-hint">
                This happens as soon as you press the button.
              </p>

              {/* No standing hint under the button (11.3) - see Turnstile.tsx. */}

              <div className="text-sm">
                <Link className="text-link hover:underline" to="/connect">
                  Cancel — keep my account
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
